import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { isMobile } from 'react-device-detect'
import { getData } from '../../api/dataSlice'

/**
 * Auto-expand the right MySite accordion on first visit.
 *
 * Reads feature flags from Redux — each accordion publishes its own flag on resolution. The hook
 * itself doesn't run any AI queries. Covers all six MySite accordions:
 *   - Timelapse (sync, role-based; no readiness flag needed)
 *   - Dashboard (async; `hasDashboard` + `dashboardReady`)
 *   - Access    (async; `hasAccessMonitoring` + `accessReady`)
 *   - Report    (async; `hasReport` + `reportReady` — includes PDF HEAD-check)
 *   - Diary     (async; `hasDiary` + `diaryReady`)
 *   - Download  (sync, role-based; no readiness flag needed, and hidden on mobile)
 *
 * Precedence (highest first):
 *   1. Chatbot has explicitly set a section (`chatbotExpandRef.current === true`) — bypass and consume the flag.
 *   2. URL pathname matches `/timelapse` or `/access` and the user has the required role/data.
 *   3. Exactly ONE of the six accordions is available → auto-expand it.
 *   4. Otherwise do nothing (show default archive grid).
 *
 * Auto-expansion fires at most once per mount (tracked by an internal ref), so async data that resolves
 * later doesn't override a user who already collapsed the expanded section. User-driven expansion/collapse
 * of any accordion also cancels auto-expand — that's why `expanded` is a gate.
 *
 * Readiness gate for case (3): ALL async features must have settled before we count, otherwise a late
 * resolver could flip "only one feature" into "two features" *after* we already auto-expanded.
 *
 * Mobile specifics:
 *   - On mobile, the Timelapse accordion doesn't actually expand (no arrow); we only flip `mysiteView`
 *     to 'timelapse' so the video grid replaces the archive grid. Desktop expands the accordion too.
 *   - Download is hidden on mobile (DownloadAccordion returns null), so `hasDownload` stays false via
 *     the initialState default — no mobile-specific logic needed here.
 */
export default function useAccordionAutoExpand({
    isMySite,
    pathname,
    timelapsePath,
    accessPath,
    expanded,
    chatbotExpandRef,
    mysiteViewRef,
    setExpanded,
    onMysiteViewChange,
}) {
    const { featureFlags } = useSelector(getData)
    const {
        hasTimelapse,
        hasDashboard, dashboardReady,
        hasAccessMonitoring, accessReady,
        hasReport, reportReady,
        hasDiary, diaryReady,
        hasDownload
    } = featureFlags

    const didAutoExpandRef = useRef(false)

    useEffect(() => {
        if (!isMySite) return

        // 1. chatbot bypass — consume the flag and exit
        if (chatbotExpandRef.current) {
            chatbotExpandRef.current = false
            return
        }

        // 2. pathname-based auto-expand for /timelapse and /access
        if (pathname === timelapsePath && hasTimelapse) {
            if (!isMobile) setExpanded('timelapse') // desktop: expand accordion
            onMysiteViewChange('timelapse') // mobile: no expansion, but activates the video grid
            return
        }
        if (pathname === accessPath && hasAccessMonitoring) {
            setExpanded('access')
            onMysiteViewChange('access')
            return
        }

        // 3. gate: wait until all async feature queries have settled, and bail if any prior expansion happened
        const allAsyncReady = dashboardReady && accessReady && reportReady && diaryReady
        if (mysiteViewRef.current // a sub-view is already active (user toggled timelapse/access already)
            || expanded // an accordion is already open
            || didAutoExpandRef.current // single-feature auto-expand already fired — don't override a later collapse
            || !allAsyncReady) { // some async feature query hasn't settled yet
            return
        }

        // 4. auto-expand the single available feature, if exactly one exists (across all six accordions)
        const availableFeatures = [
            hasTimelapse && 'timelapse',
            hasDashboard && 'dashboard',
            hasAccessMonitoring && 'access',
            hasReport && 'report',
            hasDiary && 'diary',
            hasDownload && 'download'
        ].filter(Boolean)

        if (availableFeatures.length === 1) {
            didAutoExpandRef.current = true
            const only = availableFeatures[0]
            if (only === 'timelapse') {
                if (!isMobile) setExpanded('timelapse')
                onMysiteViewChange('timelapse')
            } else if (only === 'dashboard') {
                setExpanded('dashboard')
                onMysiteViewChange('dashboard')
            } else if (only === 'access') {
                setExpanded('access')
                onMysiteViewChange('access')
            } else {
                // report | diary | download: plain accordion expand — no mysiteView side-effect (no sub-view exists)
                setExpanded(only)
            }
        }
    // `expanded` and `mysiteView` (via mysiteViewRef) are intentionally OMITTED from the dep list below:
    // including them would re-run the auto-expand on every user-driven toggle and override the user's choice.
    // setExpanded/onMysiteViewChange are stable setters and likewise excluded.
    }, [isMySite, pathname, timelapsePath, accessPath, hasTimelapse, hasDashboard, dashboardReady, hasAccessMonitoring, accessReady, hasReport, reportReady, hasDiary, diaryReady, hasDownload ]) // eslint-disable-line react-hooks/exhaustive-deps
}
